'use client'

import React, { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/lib/store/store'
import useIsMobileViewport from '@/lib/hooks/use-is-mobile-viewport'
import { useParams } from 'next/navigation'
import MobileFiltersPanel from '@/components/assets-explorer/filters/mobile-filters-panel'
import { features } from '@/lib/config'

type MainCategoryLayoutProps = PropsWithChildren<{
  categoriesNav: ReactNode
  filtersSidebar: ReactNode
  categoryTree: ReactNode
  categorySlug?: string
  subCategorySlug?: string
}>

export default function MainCategoryLayout({
  children,
  categoriesNav,
  filtersSidebar,
  categoryTree,
  categorySlug,
  subCategorySlug,
}: MainCategoryLayoutProps) {
  const routeParams = useParams<{
    mainCategory?: string
    category?: string
    subCategory?: string
  }>()
  const activeCategorySlug = routeParams.category ?? categorySlug
  const activeSubCategorySlug = routeParams.subCategory ?? subCategorySlug
  const showFilterSidebar = !!activeCategorySlug && !!activeSubCategorySlug
  const isMobileViewport = useIsMobileViewport()
  const showMobileFiltersPanel = showFilterSidebar && isMobileViewport

  const hasHydrated = useAppStore((state) => state._hasHydrated)
  const [isMounted, setIsMounted] = useState(false)
  const mainCategoryLayoutRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    const layout = mainCategoryLayoutRef.current
    const content = layout?.querySelector<HTMLElement>('.main-category-layout-content')
    if (!layout || !content) return

    const updateBannerCenter = () => {
      const { left, width } = content.getBoundingClientRect()
      document.documentElement.style.setProperty('--asset-explorer-banner-center', `${left + width / 2}px`)
    }

    updateBannerCenter()
    const resizeObserver = new ResizeObserver(updateBannerCenter)
    resizeObserver.observe(layout)
    resizeObserver.observe(content)
    window.addEventListener('resize', updateBannerCenter)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateBannerCenter)
      document.documentElement.style.removeProperty('--asset-explorer-banner-center')
    }
  }, [])

  const canRenderPersistedFilters = isMounted && hasHydrated

  if (features.compactCategoryNavigation) {
    return (
      <div ref={mainCategoryLayoutRef} className="main-category-layout tw-grid tw-min-h-[calc(100vh_-_var(--header-height))] tw-grid-cols-[minmax(0,1fr)] tw-grid-rows-[auto_auto_1fr] md:tw-grid-cols-[auto_minmax(0,1fr)] md:tw-grid-rows-[auto_1fr]">
        {categoryTree}
        <div
          data-slot="listing-sticky-toolbar"
          className="tw-sticky tw-top-[var(--header-height)] tw-z-[2] tw-min-w-0 tw-bg-[rgb(var(--background-start-rgb))] tw-px-2"
        >
          {filtersSidebar}
        </div>
        <div className="main-category-layout-content tw-min-w-0">{children}</div>
      </div>
    )
  }

  return (
    <div
      ref={mainCategoryLayoutRef}
      className={cn(
        'main-category-layout',
        'tw-grid tw-h-full tw-min-h-[calc(100vh_-_var(--header-height))] tw-grid-rows-[auto_1fr]',
        {
          'tw-grid-cols-[minmax(0,1fr)] md:tw-grid-cols-[auto_minmax(0,1fr)]': showFilterSidebar,
          'tw-grid-cols-[minmax(0,1fr)]': !showFilterSidebar,
        }
      )}
    >
      {filtersSidebar}

      <div
        className={cn(
          'tw-sticky tw-left-0 tw-top-[var(--header-height)] tw-z-[2] tw-mb-4 tw-min-w-14 tw-bg-[rgb(var(--background-start-rgb))]',
          {
            'tw-px-2': !showFilterSidebar,
            'tw-px-2 md:tw-px-0': showFilterSidebar,
          }
        )}
      >
        {categoriesNav}
        {showMobileFiltersPanel && canRenderPersistedFilters && <MobileFiltersPanel />}
      </div>

      <div className="main-category-layout-content tw-min-w-0">{children}</div>
    </div>
  )
}
